"use client";

import {
  type RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { arrayWrap } from "@/lib/array-wrap";
import type { DetailedListing } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { SaveListingButton } from "@/components/listings/buttons/save-button";
import { buildPublicCDNUrl } from "@/lib/url";
import { useHotkeys } from "react-hotkeys-hook";

export type ListingGalleryLightboxHandle = {
  open: (startMedia: DetailedListing["listingMedia"][number]) => void;
};

export type ListingGalleryLightboxProps = {
  ref?: RefObject<ListingGalleryLightboxHandle | null>;
  listing: DetailedListing;
};

const VARIANTS = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
};

const SWIPE_CONFIDENCE_THRESHOLD = 10000;

function calculateSwipe(offset: number, velocity: number) {
  return Math.abs(offset) * velocity;
}

export function ListingGalleryLightbox({
  ref,
  listing,
}: ListingGalleryLightboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [[page, direction], setPage] = useState([0, 0]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const media = listing.listingMedia?.filter((media) => !!media.image);
  const imageIndex = arrayWrap(0, media.length, page);

  function paginate(direction: number) {
    setPage([page + direction, direction]);
  }

  function open(startMedia: DetailedListing["listingMedia"][number]) {
    const index = media.findIndex((m) => m.id === startMedia.id);
    setPage([index === -1 ? 0 : index, 0]);
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
  }

  function prev() {
    paginate(-1);
  }

  function next() {
    paginate(1);
  }

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    closeButtonRef.current?.focus();

    function handleTabTrap(event: KeyboardEvent) {
      if (event.key !== "Tab") {
        return;
      }

      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleTabTrap);
    return () => {
      document.removeEventListener("keydown", handleTabTrap);
    };
  }, [isOpen]);

  useHotkeys("esc", close, { enabled: isOpen }, [isOpen]);
  useHotkeys("left", prev, { enabled: isOpen }, [isOpen, page]);
  useHotkeys("right", next, { enabled: isOpen }, [isOpen, page]);

  useEffect(() => {
    if (!isOpen || media.length <= 1) return;

    const adjacentIndices = [
      arrayWrap(0, media.length, page - 1),
      arrayWrap(0, media.length, page + 1),
    ];

    for (const idx of adjacentIndices) {
      const key = media[idx]?.image?.file.key;
      if (key) {
        const img = new Image();
        img.src = buildPublicCDNUrl(key);
      }
    }
  }, [isOpen, page, media]);

  useImperativeHandle(ref, () => ({
    open,
  }));

  if (!isOpen) {
    return null;
  }

  if (media.length === 0 || !media[imageIndex]?.image) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[1200] bg-black/95 text-white"
      role="dialog"
      aria-modal="true"
      aria-label="Listing photo gallery"
      tabIndex={-1}
      onMouseDown={close}
    >
      <header
        className="fixed top-0 left-0 right-0 h-16 z-20"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="container h-full">
          <div className="md:px-3 grid grid-cols-3 items-center h-full">
            <div className="justify-start">
              <Button
                ref={closeButtonRef}
                variant="ghost-dark"
                onClick={close}
                aria-label="Close photo gallery"
                className="hover:translate-y-0 active:translate-y-0"
              >
                <Icon icon="XClose" />
                <span className="hidden md:inline">Close</span>
              </Button>
            </div>

            <div className="text-sm text-center">
              {imageIndex + 1} / {media.length}
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost-dark"
                aria-label="Share listing"
                className="hover:translate-y-0 active:translate-y-0"
              >
                <Icon icon="Share1" />
                <span className="hidden md:block">Share</span>
              </Button>

              <SaveListingButton listing={listing} invert />
            </div>
          </div>
        </div>
      </header>

      <div
        className="relative z-10 flex h-full items-center justify-center px-3 pt-16 pb-24 md:px-6 md:pt-20 md:pb-8"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.img
            key={page}
            // biome-ignore lint/style/noNonNullAssertion: we filtered these media items so they all have an image
            src={buildPublicCDNUrl(media[imageIndex].image!.file.key)}
            className="absolute max-h-[calc(100vh-11rem)] max-w-full rounded-xl md:max-h-[80vh]"
            custom={direction}
            variants={VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 500, damping: 35 },
              opacity: { duration: 0.15 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = calculateSwipe(offset.x, velocity.x);

              if (swipe < -SWIPE_CONFIDENCE_THRESHOLD) {
                next();
              } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD) {
                prev();
              }
            }}
          />
        </AnimatePresence>

        <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-6 z-50">
          <Button
            className="h-12 w-12 rounded-full border border-white/35 bg-black/35 text-white backdrop-blur-sm hover:bg-black/55 hover:translate-y-0 active:translate-y-0 focus-visible:ring-white/80"
            variant="ghost-dark"
            onClick={prev}
            aria-label="Previous photo"
          >
            <Icon icon="ChevronLeft" />
            <span className="sr-only">Previous photo</span>
          </Button>
        </div>

        <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-6 z-50">
          <Button
            className="h-12 w-12 rounded-full border border-white/35 bg-black/35 text-white backdrop-blur-sm hover:bg-black/55 hover:translate-y-0 active:translate-y-0 focus-visible:ring-white/80"
            variant="ghost-dark"
            onClick={next}
            aria-label="Next photo"
          >
            <Icon icon="ChevronRight" />
            <span className="sr-only">Next photo</span>
          </Button>
        </div>
      </div>

      <div
        className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] md:hidden"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="mx-auto flex max-w-xs items-center justify-between rounded-full border border-white/25 bg-black/55 px-2 py-2 backdrop-blur-md">
          <Button
            variant="ghost-dark"
            size="icon"
            className="h-11 w-11 rounded-full hover:translate-y-0 active:translate-y-0"
            onClick={prev}
            aria-label="Previous photo"
          >
            <Icon icon="ChevronLeft" />
            <span className="sr-only">Previous photo</span>
          </Button>

          <span className="text-sm font-medium text-white/95">
            {imageIndex + 1} / {media.length}
          </span>

          <Button
            variant="ghost-dark"
            size="icon"
            className="h-11 w-11 rounded-full hover:translate-y-0 active:translate-y-0"
            onClick={next}
            aria-label="Next photo"
          >
            <Icon icon="ChevronRight" />
            <span className="sr-only">Next photo</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

ListingGalleryLightbox.displayName = "ListingGalleryLightbox";
